import { IconArrowLeft, IconArrowRight, IconBuildingBank, IconContract, IconInfoCircle, IconSearch } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useSeleccionarContratoAnualizado } from "@/features/seleccionar-contrato-anualizado/hooks/use-seleccionar-contrato-anualizado";
import { contratoAnualizadoApi, type ConsultaContrato } from "@/features/seleccionar-contrato-anualizado/services/contrato-anualizado.api";
import { Badge, Button, Card, Group, Heading, IconAction, Input, PageHeader, ReadOnlyBanner, Stack, Text } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { toastErrorApi } from "@/lib/toast";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/contrato-anualizado";

import { ConsultaContratoModal } from "./ConsultaContratoModal";

function formatMonto(monto: number, moneda: string): string {
  try {
    return new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda }).format(monto);
  } catch {
    return `${monto.toFixed(2)} ${moneda}`;
  }
}

/**
 * Paso previo del flujo Contrato Anualizado (CA): el solicitante captura la
 * clave del contrato anualizado (existe en SAP). Al asociarlo se muestra el
 * proveedor derivado y el importe del contrato, y se habilita continuar a la
 * carga del CFDI.
 */
export function SeleccionarContratoAnualizadoPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);

  const [clave, setClave] = useState(sol.contratoAnualizado?.clave ?? "");
  const asociar = useSeleccionarContratoAnualizado(id);

  // Visor de consulta (estilo ME23N) del contrato por su clave.
  const [consulta, setConsulta] = useState<ConsultaContrato | null>(null);
  const [consultando, setConsultando] = useState(false);
  const abrirConsulta = async () => {
    const c = clave.trim();
    if (!c) return;
    setConsultando(true);
    try {
      setConsulta(await contratoAnualizadoApi.consultar(c));
    } catch (e) {
      toastErrorApi(e);
    } finally {
      setConsultando(false);
    }
  };

  // Asociar/cambiar el contrato es exclusivo del dueño (P29: otros roles ven
  // el paso en solo lectura) y solo en los estados previos a la carga del CFDI.
  const esDueno = useEsDueno(sol);
  const puedeEditar = esDueno && (sol.estado === "SubtipoSeleccionado" || sol.estado === "ContratoAnualizadoSeleccionado");

  const contrato = sol.contratoAnualizado;
  // Invoice (factura extranjera) no tiene CFDI del SAT · el siguiente paso es cargar el PDF.
  const esInvoice = sol.subtipo === "INVOICE";

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Contrato anualizado"
        description="Contrato Anualizado · asocia el contrato existente en SAP contra el que se paga."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: id.slice(0, 8), href: `/solicitudes/${id}` },
          { label: "Contrato anualizado" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="contrato" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {!esDueno && <ReadOnlyBanner motivo="no-dueno" mensaje="El contrato anualizado asociado no puede modificarse." />}
          <Card padding="lg">
            <Stack gap="md">
              <div>
                <Heading order={5}>Clave del contrato</Heading>
                <Text size="sm" c="dimmed" className="mt-0.5">
                  Ingresa la clave del contrato anualizado (ej. 4500076912). Se busca en SAP y se deriva el proveedor.
                </Text>
              </div>

              <Group align="flex-end" gap="sm">
                <Input label="Clave del contrato" placeholder="4500076912" value={clave} onChange={(e) => setClave(e.currentTarget.value)} readOnly={!puedeEditar} wrapperClassName="flex-1" />
                <IconAction label="Consultar contrato (solo lectura)" variant="light" size="lg" loading={consultando} disabled={!clave.trim()} onClick={abrirConsulta}>
                  <IconInfoCircle size={20} />
                </IconAction>
                {puedeEditar && (
                  <Button leftSection={<IconSearch size={14} />} loading={asociar.isPending} disabled={!clave.trim()} onClick={() => asociar.mutate(clave.trim())}>
                    {asociar.isPending ? "Buscando…" : "Asociar contrato"}
                  </Button>
                )}
              </Group>

              {contrato && (
                <Card
                  padding="md"
                  style={{ borderColor: "var(--color-multiva-400)", background: "color-mix(in srgb, var(--color-multiva-600) 6%, transparent)" }}
                >
                  <Stack gap={6}>
                    <Group gap={8}>
                      <IconContract size={18} className="text-multiva-600" />
                      <Text fw={600}>Contrato {contrato.clave}</Text>
                    </Group>
                    <Group gap={8}>
                      <IconBuildingBank size={16} />
                      <Text size="sm" fw={500}>
                        {contrato.proveedorNombre}
                      </Text>
                      <Badge variant="light" color="slate">
                        #{contrato.proveedorNumero}
                      </Badge>
                      <Text size="sm" c="dimmed">
                        {contrato.proveedorRfc}
                      </Text>
                    </Group>
                    <Text size="sm">
                      Importe del contrato:{" "}
                      <Text span fw={600}>
                        {formatMonto(contrato.total, contrato.moneda)}
                      </Text>
                    </Text>
                    <Text size="xs" c="dimmed">
                      {esInvoice
                        ? "El Invoice (PDF) que cargues debe ser del mismo proveedor y su total no puede exceder el saldo disponible del contrato."
                        : "El CFDI que cargues debe ser del mismo proveedor y su total no puede exceder el saldo disponible del contrato."}
                    </Text>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Card>

          <Group justify="space-between">
            <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => navigate({ to: "/solicitudes/$id", params: { id } })}>
              Volver a la solicitud
            </Button>
            <Button
              rightSection={<IconArrowRight size={14} />}
              disabled={!contrato}
              onClick={() =>
                navigate({
                  // CA múltiples: ZIP · Invoice: PDF extranjero · único: un solo CFDI.
                  to:
                    sol.subtipo === "MULTIPLES_CFDI"
                      ? "/solicitudes/$id/cargar-zip"
                      : sol.subtipo === "INVOICE"
                        ? "/solicitudes/$id/carga-invoice"
                        : "/solicitudes/$id/cargar-cfdi",
                  params: { id },
                })
              }
            >
              {esInvoice ? "Continuar a carga del Invoice (PDF)" : "Continuar a carga de CFDI"}
            </Button>
          </Group>
        </Stack>
      </LayoutConStepper>

      <ConsultaContratoModal contrato={consulta} onClose={() => setConsulta(null)} />
    </div>
  );
}
